/**
 * SmartImprove finalized snapshot viewer — full read-only experience.
 *
 * Mounted when the URL contains `?snapshot={versionId}`. Reads exclusively
 * from the stored version document; the live engagement is never queried.
 *
 * Tabs render the same data the consultant saw at finalize time:
 *   - Executive Summary  → snapshot.business_case (or computed exec_summary)
 *   - Operational Findings → snapshot.client_inputs.issues + sites + groups
 *   - Improvement Insights → snapshot.generated.insights
 *   - Recommendations    → snapshot.recommendations_v2 + strategic_recommendations
 *   - Implementation     → snapshot.implementation_plans_v2
 *   - ROI                → snapshot.roi_dashboard
 *
 * All edit / accept / archive / re-run controls are intentionally absent —
 * snapshots are immutable.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Lock,
  Mail,
  Download,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  Target,
  Workflow,
  CheckCircle2,
  Layers,
  Award,
  Building2,
  Briefcase,
  Globe2,
  Gauge,
  DollarSign,
  Users,
  Clock,
  CalendarRange,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { ExecutiveSummaryView as SharedExecView } from "./tabs/ExecutiveSummaryTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove`;
const ax = axios.create({ baseURL: API, withCredentials: true });

const TABS = [
  { id: "exec", label: "Executive Summary", icon: Award },
  { id: "findings", label: "Operational Findings", icon: AlertTriangle },
  { id: "insights", label: "Improvement Insights", icon: Lightbulb },
  { id: "recommendations", label: "Recommendations", icon: Target },
  { id: "implementation", label: "Implementation", icon: Workflow },
  { id: "roi", label: "ROI", icon: TrendingUp },
];

const money = (n) =>
  n == null
    ? "—"
    : `USD ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const moneyShort = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `USD ${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `USD ${(v / 1_000).toFixed(0)}K`;
  return `USD ${v.toFixed(0)}`;
};
const pct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
const months = (n) => (n == null ? "—" : `${Number(n).toFixed(1)} mo`);

const PRIORITY_PILL = {
  critical: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
  high: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
  medium: "bg-[#FEFCE8] text-[#854D0E] border-[#FEF08A]",
  low: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
};
const VERDICT_BAND = {
  Recommended: "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]",
  "Recommended with conditions": "bg-[#FFFBEB] border-[#FDE68A] text-[#92400E]",
  Hold: "bg-[#FEF2F2] border-[#FECACA] text-[#991B1B]",
  Pending: "bg-[#F1F5F9] border-[#E2E8F0] text-[#475569]",
};

export default function SmartImproveSnapshotPage({
  engagementId,
  versionId,
  isLatestFinalized = false,
  onReturnToDraft,
  clientReadOnly = false,
}) {
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("exec");
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ax.get(`/engagements/${engagementId}/versions/${versionId}`)
      .then((r) => {
        if (cancelled) return;
        setVersion(r.data || null);
      })
      .catch(() => setVersion(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [engagementId, versionId]);

  const snap = version?.snapshot || {};
  const eng = snap.engagement || {};
  const engFull = snap.engagement_full || eng;
  const ci = snap.client_inputs || {};
  const gen = snap.generated || {};
  const sites = snap.sites || [];
  const groups = snap.groups || [];
  const recs = snap.recommendations_v2 || gen.recommendations || [];
  const sRecs = snap.strategic_recommendations || gen.strategic_recommendations || [];
  const insights = gen.insights || [];
  const impl = snap.implementation_plans_v2 || gen.implementation || [];
  const roi = snap.roi_dashboard || (Array.isArray(gen.roi) ? gen.roi[0] : null) || {};
  const businessCase = snap.business_case || null;
  const isLegacySnapshot = !businessCase;

  const downloadUrl = useMemo(
    () =>
      version && engagementId
        ? `${API}/engagements/${engagementId}/versions/${version.id}/report.pdf`
        : null,
    [version, engagementId]
  );

  if (loading) {
    return (
      <div className="p-6 text-sm text-[#6B7280]">Loading finalized snapshot…</div>
    );
  }
  if (!version) {
    return (
      <div className="p-6 text-sm text-[#B91C1C]">
        Finalized version not found.{" "}
        <button onClick={onReturnToDraft} className="underline">
          Return to draft
        </button>
      </div>
    );
  }

  const finalizedAt = version.created_at
    ? new Date(version.created_at).toLocaleString()
    : "—";
  const finalizedBy = version.created_by?.email || "—";

  return (
    <div className="p-6 space-y-5" data-testid="smartimprove-snapshot-page">
      {clientReadOnly ? null : (
        <Link
          to="/consultant/smartimprove"
          className="inline-flex items-center gap-1 text-xs text-[#025979] hover:text-[#03738C]"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to SmartImprove
        </Link>
      )}

      <section
        className="bg-[#FFF7ED] border border-[#FED7AA] rounded-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3"
        data-testid="snapshot-readonly-banner"
      >
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#9A3412]" />
          <div>
            <p className="text-sm font-bold text-[#9A3412]">
              Viewing Finalized Version v{version.version_number} (Read-only)
            </p>
            <p className="text-[11px] text-[#B45309]">
              Finalized {finalizedAt} · by {finalizedBy}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {downloadUrl && isLatestFinalized && !clientReadOnly ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEmailConfirmOpen(true)}
              className="rounded-sm text-xs h-8 border-[#025979] text-[#025979] hover:bg-[#E0F2FE]"
              data-testid="snapshot-send-pdf-btn"
            >
              <Mail className="w-3 h-3 mr-1" /> Send Final PDF
            </Button>
          ) : null}
          {downloadUrl ? (
            <a
              href={downloadUrl}
              download
              className="inline-flex items-center gap-1 px-3 h-8 text-[11px] font-semibold bg-[#025979] hover:bg-[#024a63] text-white rounded-sm"
              data-testid="snapshot-download-pdf-btn"
            >
              <Download className="w-3 h-3" /> Download Final PDF
            </a>
          ) : null}
          {clientReadOnly ? null : (
            <Button
              size="sm"
              variant="outline"
              onClick={onReturnToDraft}
              className="rounded-sm text-xs h-8 border-[#9A3412] text-[#9A3412] hover:bg-white"
              data-testid="snapshot-return-to-draft"
            >
              Return to Draft
            </Button>
          )}
        </div>
      </section>

      {isLegacySnapshot ? (
        <section
          className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-sm px-4 py-3 text-[12px] text-[#475569]"
          data-testid="snapshot-legacy-notice"
        >
          This version contains limited snapshot data because it was finalized
          before full snapshot capture was enabled. Re-finalize the engagement
          to capture the boardroom Executive Summary view.
        </section>
      ) : null}

      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            WarehouseX · SmartImprove · Finalized Snapshot
          </p>
          <h1
            className="text-2xl font-bold text-[#111827]"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="snapshot-title"
          >
            {eng.company_name || "Engagement"}
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            {[engFull.contact_name, engFull.email, engFull.industry, engFull.geography || engFull.geographic_distribution]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </header>

      <nav
        className="flex items-center gap-1 border-b border-[#E5E7EB] flex-wrap"
        data-testid="snapshot-tabs"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-xs font-semibold border-b-2 transition-colors inline-flex items-center gap-1.5 ${
                t.id === tab
                  ? "text-[#025979] border-[#025979]"
                  : "text-[#374151] border-transparent hover:text-[#025979]"
              }`}
              data-testid={`snapshot-tab-${t.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </nav>

      {tab === "exec" ? (
        <ExecutiveSummaryView businessCase={businessCase} fallbackRoi={roi} fallbackImpl={impl} fallbackRecs={recs} fallbackSRecs={sRecs} />
      ) : null}
      {tab === "findings" ? (
        <FindingsView issues={ci.issues || []} sites={sites} groups={groups} />
      ) : null}
      {tab === "insights" ? (
        <InsightsView insights={insights} groups={groups} />
      ) : null}
      {tab === "recommendations" ? (
        <RecommendationsView recs={recs} sRecs={sRecs} />
      ) : null}
      {tab === "implementation" ? <ImplementationView impl={impl} /> : null}
      {tab === "roi" ? <RoiView roi={roi} /> : null}

      <SnapshotEmailConfirmDialog
        open={emailConfirmOpen}
        onOpenChange={setEmailConfirmOpen}
        onConfirm={() => {
          setEmailConfirmOpen(false);
          setEmailOpen(true);
        }}
      />
      {emailOpen && version ? (
        <EmailDialog
          engagementId={engagementId}
          versionId={version.id}
          open={emailOpen}
          onOpenChange={setEmailOpen}
        />
      ) : null}
    </div>
  );
}

// ===========================================================================
// Executive Summary — mirrors the live ExecutiveSummaryTab business case view
// ===========================================================================
function ExecutiveSummaryView({ businessCase, fallbackRoi, fallbackImpl, fallbackRecs, fallbackSRecs }) {
  if (businessCase) {
    return <FullBusinessCase bc={businessCase} />;
  }
  // Legacy fallback for old snapshots that don't yet have business_case.
  const inv = fallbackRoi?.investment || {};
  const finalizedImpl = (fallbackImpl || []).filter(
    (p) => (p.status || "").toLowerCase() === "finalized"
  );
  const totalImpl = finalizedImpl.reduce(
    (a, p) => a + (Number(p.total_cost_usd) || 0),
    0
  );
  return (
    <div className="space-y-4">
      <SectionCard title="Top KPIs">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <KpiBox label="Total Investment" value={money(inv.one_time)} />
          <KpiBox label="Annual Savings" value={money(fallbackRoi?.annual_savings_usd)} />
          <KpiBox label="Payback" value={months(fallbackRoi?.overall_payback_months)} />
          <KpiBox label="3-Year ROI" value={pct(fallbackRoi?.overall_roi_percent)} hero />
        </div>
      </SectionCard>
      <SectionCard title="Snapshot Overview">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <Stat label="Recommendations" value={(fallbackRecs?.length || 0) + (fallbackSRecs?.length || 0)} />
          <Stat label="Strategic Recs" value={fallbackSRecs?.length || 0} />
          <Stat label="Finalized Plans" value={finalizedImpl.length} />
          <Stat label="Implementation USD" value={money(totalImpl)} />
        </div>
      </SectionCard>
    </div>
  );
}

function FullBusinessCase({ bc }) {
  // Reuse the EXACT live Executive Summary tab renderer so the snapshot view
  // never drifts from the working tab.
  return (
    <div data-testid="snapshot-exec-business-case">
      <SharedExecView bc={bc} isFinalized />
    </div>
  );
}

function ExecHeader({ header }) {
  if (!header) return null;
  const meta = [header.industry, header.business_model].filter(Boolean);
  return (
    <article
      className="bg-gradient-to-br from-[#0B2A45] to-[#025979] text-white border border-[#01445E] rounded-sm p-6"
      data-testid="snap-exec-header"
    >
      <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#67E8F9] mb-1">
        Operational Improvement Business Case · Executive Decision Report
      </p>
      <h2
        className="text-2xl font-bold mb-1"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {header.company_name}
      </h2>
      {meta.length ? <p className="text-[11px] text-[#BAE6FD]">{meta.join(" · ")}</p> : null}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        <HeaderTile icon={Briefcase} label="Strategic Objective" value={header.strategic_objective} />
        <HeaderTile icon={Globe2} label="Geography" value={header.geography} subtext={(header.countries || []).join(", ")} />
        <HeaderTile icon={Building2} label="Sites in Scope" value={String(header.site_count || "—")} />
        <HeaderTile icon={Gauge} label="Priority" value={(header.priority || "—").toUpperCase()} />
      </div>
    </article>
  );
}
function HeaderTile({ icon: Icon, label, value, subtext }) {
  return (
    <div className="bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.15)] rounded-sm px-3 py-2">
      <div className="flex items-center gap-1.5 text-[#67E8F9] text-[10px] uppercase tracking-wider mb-0.5">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <p className="text-sm font-semibold text-white">{value || "—"}</p>
      {subtext ? <p className="text-[10px] text-[#BAE6FD] mt-0.5">{subtext}</p> : null}
    </div>
  );
}

function KpiStrip({ kpi }) {
  if (!kpi) return null;
  const tiles = [
    { icon: DollarSign, label: "Total Investment", primary: moneyShort(kpi.total_investment_usd), secondary: kpi.total_recurring_usd ? `+${moneyShort(kpi.total_recurring_usd)} / yr recurring` : "One-time", accent: "#025979" },
    { icon: TrendingUp, label: "Annual Savings", primary: moneyShort(kpi.annual_savings_usd), secondary: "Confidence-adjusted", accent: "#047857" },
    { icon: Clock, label: "Payback", primary: months(kpi.payback_months), secondary: "Time to break-even", accent: "#C2410C" },
    { icon: Award, label: "3-Year ROI", primary: pct(kpi.three_year_roi_pct), secondary: "Net return", accent: "#6E5BCE" },
    { icon: Users, label: "Peak Resources", primary: String(kpi.peak_resources || 0), secondary: "Max concurrent team", accent: "#0E7490" },
    { icon: CalendarRange, label: "Total Duration", primary: `${kpi.total_duration_weeks || 0} wks`, secondary: `${kpi.finalized_plan_count || 0} finalized plans`, accent: "#1D4ED8" },
  ];
  return (
    <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="snap-kpi-strip">
      {tiles.map((t, i) => {
        const Icon = t.icon;
        return (
          <article key={i} className="bg-white border border-[#E5E7EB] rounded-sm p-3 relative overflow-hidden">
            <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: t.accent }} />
            <div className="ml-1.5">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </div>
              <p className="text-lg font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>
                {t.primary}
              </p>
              <p className="text-[10px] text-[#6B7280] mt-0.5">{t.secondary}</p>
            </div>
          </article>
        );
      })}
    </section>
  );
}

function Challenges({ challenges }) {
  if (!challenges?.length) return null;
  return (
    <Section icon={AlertTriangle} title="Current Operational Challenges" subtitle="Findings clustered by workstream.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {challenges.map((c) => (
          <article key={c.key} className="bg-white border border-[#E5E7EB] rounded-sm p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                {c.group_code ? (
                  <span className="text-[10px] font-semibold text-[#025979] tracking-wider">{c.group_code}</span>
                ) : null}
                <h4 className="text-sm font-semibold text-[#111827]">{c.title}</h4>
              </div>
              <span className="text-[10px] uppercase tracking-wider bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0] px-1.5 py-0.5 rounded-sm">
                {c.kind === "workstream" ? "Workstream" : "Area"}
              </span>
            </div>
            <p className="text-[11px] text-[#6B7280] mb-2">
              <strong className="text-[#111827]">{c.finding_count}</strong>{" "}
              finding{c.finding_count === 1 ? "" : "s"}
              {c.site_count ? ` · ${c.site_count} site${c.site_count === 1 ? "" : "s"}` : ""}
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {c.critical_count ? <PriorityChip count={c.critical_count} priority="critical" /> : null}
              {c.high_count ? <PriorityChip count={c.high_count} priority="high" /> : null}
              {c.medium_count ? <PriorityChip count={c.medium_count} priority="medium" /> : null}
              {c.low_count ? <PriorityChip count={c.low_count} priority="low" /> : null}
            </div>
            <ul className="space-y-1 text-xs text-[#374151] border-t border-[#F1F5F9] pt-2">
              {(c.examples || []).map((ex, i) => (
                <li key={i}>
                  <span className="text-[#6B7280] mr-1">{ex.finding_code}</span>
                  {ex.title}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </Section>
  );
}

function PriorityChip({ count, priority }) {
  return (
    <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${PRIORITY_PILL[priority]}`}>
      {count} {priority}
    </span>
  );
}

function Narrative({ paragraphs }) {
  if (!paragraphs?.length) return null;
  return (
    <Section icon={Lightbulb} title="Executive Improvement Narrative" subtitle="Board-level frame for the transformation.">
      <div className="space-y-3 text-sm text-[#374151] leading-relaxed bg-white border border-[#E5E7EB] rounded-sm p-5">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </Section>
  );
}

function Portfolio({ rows }) {
  if (!rows?.length) return null;
  return (
    <Section icon={Target} title="Recommendation Portfolio Summary" subtitle={`${rows.length} recommendation${rows.length === 1 ? "" : "s"}.`}>
      <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="px-3 py-2 font-semibold">Code</th>
              <th className="px-3 py-2 font-semibold">Recommendation</th>
              <th className="px-3 py-2 font-semibold">Kind</th>
              <th className="px-3 py-2 font-semibold">Type</th>
              <th className="px-3 py-2 font-semibold">Complexity</th>
              <th className="px-3 py-2 font-semibold text-right">Investment</th>
              <th className="px-3 py-2 font-semibold text-right">Duration</th>
              <th className="px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className="border-t border-[#F1F5F9]">
                <td className="px-3 py-2 font-semibold text-[#025979] text-xs">{r.code}</td>
                <td className="px-3 py-2 text-[#111827]">
                  <p className="font-medium">{r.title}</p>
                  {r.workstream ? <p className="text-[10px] text-[#6B7280] mt-0.5">{r.workstream}</p> : null}
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded-sm border ${r.kind === "Strategic" ? "bg-[#F0EDF9] border-[#DDD6FE] text-[#473F8B]" : "bg-[#EFF6FF] border-[#BFDBFE] text-[#1D4ED8]"}`}>
                    {r.kind}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-[#374151]">{r.type}</td>
                <td className="px-3 py-2 text-xs text-[#374151]">{r.complexity}</td>
                <td className="px-3 py-2 text-xs text-right text-[#111827]">{r.investment_usd != null ? moneyShort(r.investment_usd) : "—"}</td>
                <td className="px-3 py-2 text-xs text-right text-[#111827]">{r.duration_weeks ? `${r.duration_weeks} wks` : "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${(r.status || "").toLowerCase() === "accepted" ? "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]" : "bg-[#F1F5F9] border-[#E2E8F0] text-[#475569]"}`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function ValueDrivers({ drivers }) {
  if (!drivers?.length) return null;
  const maxShare = Math.max(...drivers.map((d) => d.share_pct || 0), 1);
  return (
    <Section icon={Workflow} title="Top Value Drivers" subtitle="Where operational savings concentrate.">
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-4 space-y-3">
        {drivers.map((d) => (
          <div key={d.key}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-[#111827]">{d.label}</span>
              <span className="text-[#6B7280]">
                {moneyShort(d.amount_usd)} · {pct(d.share_pct)} · {d.rec_count} rec{d.rec_count === 1 ? "" : "s"}
              </span>
            </div>
            <div className="w-full h-2 bg-[#F1F5F9] rounded-sm overflow-hidden">
              <div className="h-full bg-[#025979]" style={{ width: `${Math.max((d.share_pct / maxShare) * 100, 2)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Decision({ decision }) {
  if (!decision) return null;
  const band = VERDICT_BAND[decision.verdict] || VERDICT_BAND.Pending;
  return (
    <Section icon={Award} title="Executive Decision Statement" subtitle="The boardroom recommendation.">
      <article className={`border rounded-sm p-5 ${band}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em]">Verdict</span>
          <span className="text-sm font-bold px-2 py-0.5 rounded-sm bg-white/60 border border-current/30">
            {decision.verdict}
          </span>
        </div>
        <p className="text-base font-semibold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          {decision.headline}
        </p>
        <p className="text-sm leading-relaxed mb-3">{decision.body}</p>
        {decision.asks?.length ? (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5">
              Board is asked to:
            </p>
            <ol className="space-y-1 text-sm list-decimal list-inside">
              {decision.asks.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ol>
          </div>
        ) : null}
      </article>
    </Section>
  );
}

function EvidenceAppendix({ chain }) {
  if (!chain?.length) return null;
  return (
    <Section icon={Layers} title="Appendix · Evidence Chain" subtitle="Issue → Observations → KPI → Insight → Recommendation traceability.">
      <ol className="space-y-4 bg-white border border-[#E5E7EB] rounded-sm p-5">
        {chain.map((node, i) => (
          <li key={i} className="relative pl-6 border-l-2 border-[#E5E7EB]">
            <span className="absolute -left-[9px] top-0 flex items-center justify-center w-4 h-4 rounded-full bg-[#025979] text-white text-[10px] font-bold">
              {i + 1}
            </span>
            <h4 className="text-sm font-semibold text-[#111827]">{node.insight_title}</h4>
            {(node.supporting_observations?.length || 0) > 0 ? (
              <p className="text-xs text-[#374151] mt-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mr-2">Observations</span>
                {node.supporting_observations.slice(0, 3).map((o) => o.note).join("; ")}
              </p>
            ) : null}
            {(node.linked_kpis?.length || 0) > 0 ? (
              <p className="text-xs text-[#374151] mt-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mr-2">KPIs</span>
                {node.linked_kpis.join(", ")}
              </p>
            ) : null}
            {(node.recommendations?.length || 0) > 0 ? (
              <p className="text-xs text-[#374151] mt-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mr-2">Recommendations</span>
                {node.recommendations.map((r) => r.title).join("; ")}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </Section>
  );
}

// ===========================================================================
// Operational Findings — full table mirroring the live PRIVA findings view
// ===========================================================================
function FindingsView({ issues, sites, groups }) {
  if (!issues.length) return <Empty msg="No findings captured in this snapshot." />;
  const groupByCode = Object.fromEntries(groups.map((g) => [g.group_code, g.title]));
  const groupById = Object.fromEntries(groups.map((g) => [g.id, g.title]));
  const siteByCode = Object.fromEntries(sites.map((s) => [s.site_code, s.site_name]));
  return (
    <SectionCard title={`Operational Findings (${issues.length})`} data-testid="snap-findings-card">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="px-2 py-2 font-semibold">Code</th>
              <th className="px-2 py-2 font-semibold">Title</th>
              <th className="px-2 py-2 font-semibold">Op Area</th>
              <th className="px-2 py-2 font-semibold">Sub-process</th>
              <th className="px-2 py-2 font-semibold">Priority</th>
              <th className="px-2 py-2 font-semibold">Workstream</th>
              <th className="px-2 py-2 font-semibold">Sites</th>
              <th className="px-2 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {issues.map((f) => {
              const ws = groupByCode[f.enhancement_group_code] || groupById[f.enhancement_group_id] || "—";
              const siteList = (f.applicable_site_ids || []).map((id) => {
                const s = sites.find((x) => x.id === id);
                return s ? (s.site_code || s.site_name) : "—";
              }).filter(Boolean);
              return (
                <tr key={f.id || f.finding_code} className="border-t border-[#F1F5F9]">
                  <td className="px-2 py-2 font-semibold text-[#025979]">{f.finding_code || "—"}</td>
                  <td className="px-2 py-2 text-[#111827]">{f.title || "—"}</td>
                  <td className="px-2 py-2 text-[#374151]">{f.operational_area || "—"}</td>
                  <td className="px-2 py-2 text-[#374151]">{f.sub_process || "—"}</td>
                  <td className="px-2 py-2">
                    <span className={`px-1.5 py-0.5 text-[10px] uppercase rounded-sm border ${PRIORITY_PILL[(f.priority || f.severity || "medium").toLowerCase()] || PRIORITY_PILL.medium}`}>
                      {f.priority || f.severity || "medium"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-[#374151]">
                    {f.enhancement_group_code ? (
                      <>
                        <span className="text-[10px] font-semibold text-[#025979]">{f.enhancement_group_code}</span>
                        <span className="ml-1">{ws}</span>
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-2 py-2 text-[#374151]">{siteList.length ? siteList.join(", ") : "—"}</td>
                  <td className="px-2 py-2 text-[#374151] capitalize">{f.status || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

// ===========================================================================
// Improvement Insights — grouped by workstream
// ===========================================================================
function InsightsView({ insights, groups }) {
  if (!insights.length) return <Empty msg="No improvement insights captured in this snapshot." />;
  const groupByCode = Object.fromEntries(groups.map((g) => [g.group_code, g.title]));
  // Group insights by ws_code.
  const grouped = insights.reduce((acc, i) => {
    const key = i.ws_code || i.workstream_code || "OTHER";
    if (!acc[key]) acc[key] = [];
    acc[key].push(i);
    return acc;
  }, {});
  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([wsCode, items]) => (
        <SectionCard
          key={wsCode}
          title={
            <span className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-[#025979] tracking-wider">{wsCode}</span>
              <span className="font-semibold">{groupByCode[wsCode] || "Workstream"}</span>
              <span className="text-[10px] text-[#6B7280] font-normal">({items.length} insight{items.length === 1 ? "" : "s"})</span>
            </span>
          }
        >
          <ul className="space-y-3">
            {items.map((i) => (
              <li key={i.id || i.title} className="border-b border-dashed border-[#F1F5F9] pb-2 last:border-b-0">
                <p className="text-sm font-semibold text-[#0F172A] mb-1">{i.title || "—"}</p>
                {i.summary ? <p className="text-[12px] text-[#374151]">{i.summary}</p> : null}
                {i.impact_area ? <p className="text-[10px] text-[#6B7280] mt-1 uppercase tracking-wider">{i.impact_area}</p> : null}
              </li>
            ))}
          </ul>
        </SectionCard>
      ))}
    </div>
  );
}

// ===========================================================================
// Recommendations — operational + strategic with full detail
// ===========================================================================
function RecommendationsView({ recs, sRecs }) {
  if (!recs.length && !sRecs.length) {
    return <Empty msg="No recommendations captured in this snapshot." />;
  }
  return (
    <div className="space-y-4">
      {recs.length > 0 ? (
        <SectionCard title={`Operational Recommendations (${recs.length})`}>
          <div className="space-y-3">
            {recs.map((r) => (
              <RecCard key={r.id || r.rec_code} r={r} />
            ))}
          </div>
        </SectionCard>
      ) : null}
      {sRecs.length > 0 ? (
        <SectionCard title={`Strategic Recommendations (${sRecs.length})`}>
          <div className="space-y-3">
            {sRecs.map((r) => (
              <RecCard key={r.id || r.srec_code} r={r} strategic />
            ))}
          </div>
        </SectionCard>
      ) : null}
    </div>
  );
}
function RecCard({ r, strategic }) {
  return (
    <article className="bg-white border border-[#E5E7EB] rounded-sm p-3">
      <div className="flex items-start justify-between gap-2 mb-1.5 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold tracking-wider text-[#025979]">
            {r.rec_code || r.srec_code}
          </span>
          <span className="text-sm font-semibold text-[#0F172A]">{r.title || "—"}</span>
          {strategic ? (
            <span className="text-[9px] font-semibold text-[#473F8B] bg-[#F0EDF9] border border-[#DDD6FE] px-1.5 py-0.5 rounded-sm">
              Strategic
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5">
          {r.recommendation_type ? (
            <span className="text-[10px] bg-[#EFF6FF] border border-[#BFDBFE] text-[#1D4ED8] px-1.5 py-0.5 rounded-sm">
              {r.recommendation_type}
            </span>
          ) : null}
          {r.complexity ? (
            <span className="text-[10px] bg-[#F1F5F9] border border-[#E2E8F0] text-[#475569] px-1.5 py-0.5 rounded-sm">
              {r.complexity}
            </span>
          ) : null}
          {r.status ? (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border uppercase tracking-wider ${(r.status || "").toLowerCase() === "accepted" ? "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]" : "bg-[#F1F5F9] border-[#E2E8F0] text-[#475569]"}`}>
              {r.status}
            </span>
          ) : null}
        </div>
      </div>
      {r.summary ? <p className="text-[12px] text-[#374151] mb-1">{r.summary}</p> : null}
      {r.expected_business_impact || r.expected_benefit ? (
        <p className="text-[11px] text-[#475569]">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mr-1.5">Expected Impact</span>
          {r.expected_business_impact || r.expected_benefit}
        </p>
      ) : null}
      {r.linked_workstream_title ? (
        <p className="text-[10px] text-[#6B7280] mt-1">Workstream: {r.linked_workstream_title}</p>
      ) : null}
    </article>
  );
}

// ===========================================================================
// Implementation — finalized plans with detail
// ===========================================================================
function ImplementationView({ impl }) {
  const finalized = (impl || []).filter((p) => (p.status || "").toLowerCase() === "finalized");
  if (!finalized.length) {
    return <Empty msg="No finalized implementation plans in this snapshot." />;
  }
  const totalCost = finalized.reduce((a, p) => a + (Number(p.total_cost_usd) || 0), 0);
  const totalWeeks = finalized.reduce((a, p) => a + Math.ceil(Number(p.estimated_weeks) || 0), 0);
  const totalHours = finalized.reduce((a, p) => a + (Number(p.total_effort_hours) || 0), 0);
  const peakResources = Math.max(...finalized.map((p) => (p.resources || []).filter((r) => (r.role || "").trim()).length), 0);

  return (
    <div className="space-y-4">
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="snap-impl-kpi">
        <KpiBox label="Finalized Plans" value={finalized.length} />
        <KpiBox label="Total Investment" value={money(totalCost)} />
        <KpiBox label="Total Duration" value={`${totalWeeks} wks`} />
        <KpiBox label="Peak Resources" value={peakResources} hero />
      </section>
      <SectionCard title={`Implementation Plans (${finalized.length})`} header={<span className="text-[11px] text-[#0F172A]">{Math.round(totalHours)} effort hrs</span>}>
        <div className="space-y-3">
          {finalized.map((p) => (
            <article key={p.id || p.rec_code} className="bg-white border border-[#E5E7EB] rounded-sm p-3">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold tracking-wider text-[#025979]">{p.rec_code}</span>
                  <span className="text-sm font-semibold text-[#0F172A]">{p.rec_title}</span>
                </div>
                <span className="text-[10px] uppercase tracking-wider bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] px-1.5 py-0.5 rounded-sm">
                  Finalized
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] mb-2">
                <Stat label="Template" value={p.template_name || p.template_code || "—"} />
                <Stat label="Duration" value={`${Math.ceil(Number(p.estimated_weeks) || 0)} wks`} />
                <Stat label="Total Cost" value={money(p.total_cost_usd)} />
                <Stat label="Effort" value={`${Math.round(Number(p.total_effort_hours) || 0)} hrs`} />
              </div>
              {(p.resources || []).length > 0 ? (
                <div className="border-t border-[#F1F5F9] pt-2 mt-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1">Resources ({(p.resources || []).length})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(p.resources || []).map((r, i) => (
                      <span key={i} className="text-[10px] bg-[#F8FAFC] border border-[#E5E7EB] text-[#374151] px-1.5 py-0.5 rounded-sm">
                        {r.role || "Role"} · {Math.round(Number(r.hours) || 0)}h
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

// ===========================================================================
// ROI — full dashboard reproduction
// ===========================================================================
function RoiView({ roi }) {
  if (!roi || roi.is_empty) return <Empty msg="No ROI captured in this snapshot." />;
  const sby = roi.savings_by_year || {};
  const drivers = roi.value_drivers || [];
  return (
    <div className="space-y-4">
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="snap-roi-kpi">
        <KpiBox label="Total Investment" value={money(roi.investment?.one_time)} />
        <KpiBox label="Annual Savings" value={money(roi.annual_savings_usd)} />
        <KpiBox label="Payback" value={months(roi.overall_payback_months)} />
        <KpiBox label="3-Year ROI" value={pct(roi.overall_roi_percent)} hero />
      </section>
      {drivers.length > 0 ? (
        <SectionCard title="Value Drivers">
          <table className="w-full text-xs">
            <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-2 py-1.5 font-semibold">Driver</th>
                <th className="px-2 py-1.5 font-semibold text-right">Annual Savings</th>
                <th className="px-2 py-1.5 font-semibold text-right">Recs</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => (
                <tr key={d.key} className="border-t border-[#F1F5F9]">
                  <td className="px-2 py-1.5">{d.label || d.key}</td>
                  <td className="px-2 py-1.5 text-right">{money(d.amount)}</td>
                  <td className="px-2 py-1.5 text-right">{d.rec_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      ) : null}
      <SectionCard title="Benefit Realization Timeline">
        <div className="grid grid-cols-3 gap-2">
          <KpiBox label="Year 1 (ramp)" value={money(sby.year1)} compact />
          <KpiBox label="Year 2 (steady)" value={money(sby.year2)} compact />
          <KpiBox label="Year 3 (steady)" value={money(sby.year3)} compact />
        </div>
        <p className="text-[11px] text-[#6B7280] mt-2">
          3-yr total <strong className="text-[#0F172A]">{money(sby.three_year_total)}</strong> · 3-yr net{" "}
          <strong className="text-[#0F172A]">{money(sby.three_year_net)}</strong>
        </p>
      </SectionCard>
    </div>
  );
}

// ===========================================================================
// Email confirm dialog — lightweight popup matching SmartEvaluate UX
// ===========================================================================
function SnapshotEmailConfirmDialog({ open, onOpenChange, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-sm"
        data-testid="snapshot-email-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-sm font-semibold text-[#111827]"
            style={{ fontFamily: "Chivo" }}
          >
            Send SmartImprove report via email?
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[#6B7280] leading-relaxed">
            The finalized SmartImprove Executive Report will be shared as a PDF attachment.<br />
            You can review the recipient details before sending.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8 px-3 border-[#E5E7EB]"
            onClick={() => onOpenChange(false)}
            data-testid="snapshot-email-confirm-no-btn"
          >
            No, go back
          </Button>
          <Button
            size="sm"
            className="text-xs h-8 px-3 bg-[#025979] hover:bg-[#024a63] text-white"
            onClick={onConfirm}
            data-testid="snapshot-email-confirm-yes-btn"
          >
            <Mail className="w-3 h-3 mr-1.5" /> Yes, compose
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Email dialog
// ===========================================================================
function EmailDialog({ engagementId, versionId, open, onOpenChange }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const target = (to || "").trim();
    if (!target) {
      toast.error("Recipient email is required");
      return;
    }
    setSending(true);
    try {
      await ax.post(
        `/engagements/${engagementId}/versions/${versionId}/email-report`,
        { to: target, message: message || undefined }
      );
      toast.success(`Final PDF sent to ${target}`);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send PDF");
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="snapshot-email-dialog">
        <DialogHeader>
          <DialogTitle>Send Final PDF by Email</DialogTitle>
          <DialogDescription>
            Sends the finalized SmartImprove Executive PDF as an attachment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-[10px] uppercase font-semibold text-[#6B7280] tracking-wider mb-1 block">
              Recipient
            </label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="cfo@client.com"
              className="rounded-sm text-sm"
              data-testid="snapshot-email-to"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-[#6B7280] tracking-wider mb-1 block">
              Optional message
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="rounded-sm text-sm"
              data-testid="snapshot-email-message"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm text-xs" data-testid="snapshot-email-cancel">
            Cancel
          </Button>
          <Button onClick={send} disabled={sending} className="bg-[#025979] hover:bg-[#024a63] text-white rounded-sm text-xs" data-testid="snapshot-email-send">
            {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Shared UI primitives
// ===========================================================================
function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section>
      <header className="flex items-center gap-2 mb-1">
        {Icon ? <Icon className="w-4 h-4 text-[#025979]" /> : null}
        <h3 className="text-base font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>
          {title}
        </h3>
      </header>
      {subtitle ? <p className="text-[11px] text-[#6B7280] mb-3 -mt-0.5">{subtitle}</p> : null}
      {children}
    </section>
  );
}

function SectionCard({ title, children, header }) {
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-sm">
      <header className="px-4 py-2.5 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#025979]">{title}</p>
        {header}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function KpiBox({ label, value, hero = false, compact = false }) {
  if (hero) {
    return (
      <div className="bg-[#025979] text-white rounded-sm px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-wider text-white/70">{label}</p>
        <p className="text-lg font-extrabold mt-0.5">{value}</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</p>
      <p className={`${compact ? "text-sm" : "text-base"} font-bold text-[#0F172A] mt-0.5 tabular-nums`}>
        {value}
      </p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</p>
      <p className="text-sm font-bold text-[#0F172A] mt-0.5">{value}</p>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="bg-white border border-dashed border-[#E5E7EB] rounded-sm py-12 text-center">
      <CheckCircle2 className="w-6 h-6 text-[#94A3B8] mx-auto mb-2" />
      <p className="text-xs text-[#6B7280]">{msg}</p>
    </div>
  );
}
